import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, Save, Phone, Mail, Building2, Tag, DollarSign, Clock, Send, StickyNote } from "lucide-react";

const STATUSES = ["new", "contacted", "interested", "won", "lost"];
const STATUS_BADGE = {
  new: "badge-upcoming", contacted: "badge-upcoming", interested: "badge-healthy",
  won: "badge-healthy", lost: "badge-overdue",
};
const ACT_ICON = { note: StickyNote, call: Phone, email: Mail, meeting: Clock, status: Tag };

export default function AdminLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null);
  const [activity, setActivity] = useState("");
  const [actType, setActType] = useState("note");

  const load = () => api.get(`/admin/crm/leads/${id}`).then(({ data }) => { setLead(data); setForm({
    name: data.name || "", email: data.email || "", phone: data.phone || "", company: data.company || "",
    source: data.source || "", value: data.value ?? "", notes: data.notes || "",
  }); }).catch(() => toast.error("Could not load lead"));
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.patch(`/admin/crm/leads/${id}`, { ...form, value: form.value === "" ? null : parseFloat(form.value) });
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };

  const setStatus = async (status) => {
    try { await api.patch(`/admin/crm/leads/${id}`, { status }); load(); }
    catch { toast.error("Could not update status"); }
  };

  const addActivity = async () => {
    if (!activity.trim()) return;
    try { await api.post(`/admin/crm/leads/${id}/activity`, { type: actType, text: activity.trim() }); setActivity(""); load(); }
    catch { toast.error("Could not log activity"); }
  };

  const del = async () => {
    try { await api.delete(`/admin/crm/leads/${id}`); toast.success("Lead deleted"); nav("/admin/crm"); }
    catch { toast.error("Could not delete"); }
  };

  const EMAIL_TEMPLATES = [
    { key: "demo_followup", label: "Demo follow-up" },
    { key: "getting_started", label: "Getting started" },
    { key: "check_in", label: "Just checking in" },
  ];
  const [template, setTemplate] = useState("demo_followup");
  const [emailNote, setEmailNote] = useState("");
  const [sending, setSending] = useState(false);
  const sendEmail = async () => {
    setSending(true);
    try {
      await api.post(`/admin/crm/leads/${id}/email`, { template, note: emailNote.trim() || null });
      toast.success("Email sent");
      setEmailNote(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send email"); }
    finally { setSending(false); }
  };

  if (!lead || !form) return <div className="p-5 md:p-10" data-testid="admin-lead-page"><p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p></div>;

  return (
    <div className="p-5 md:p-10 pb-24 max-w-3xl" data-testid="admin-lead-page">
      <button onClick={() => nav("/admin/crm")} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4" data-testid="lead-back"><ArrowLeft className="w-4 h-4" /> All leads</button>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="font-head font-900 uppercase tracking-tight text-3xl">{lead.name}</h1>
          <p className="font-mono text-xs text-[#94A3B8]">Added {fmtDateTime(lead.created_at)} · by {lead.created_by}</p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild><button className="btn-ghost h-10 px-3 border border-white/15 text-[#94A3B8] hover:text-[#FF3B30]" data-testid="lead-delete"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
          <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white">
            <AlertDialogHeader><AlertDialogTitle className="font-head uppercase">Delete {lead.name}?</AlertDialogTitle><AlertDialogDescription className="text-[#94A3B8]">This permanently removes the lead and its activity.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="btn-ghost border-white/20">Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid="lead-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Pipeline status */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="lead-status-row">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`badge ${lead.status === s ? STATUS_BADGE[s] : "border border-white/15 text-[#94A3B8]"}`} data-testid={`lead-status-${s}`}>{s}</button>
        ))}
      </div>

      {/* Details */}
      <div className="card p-5 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lead-details">
        <div className="sm:col-span-2"><label className="il-label">Name</label><input className="il-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
        <div><label className="il-label"><Mail className="w-3 h-3 inline mr-1" />Email</label><input className="il-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" /></div>
        <div><label className="il-label"><Phone className="w-3 h-3 inline mr-1" />Phone</label><input className="il-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" /></div>
        <div><label className="il-label"><Building2 className="w-3 h-3 inline mr-1" />Company</label><input className="il-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company" /></div>
        <div><label className="il-label"><Tag className="w-3 h-3 inline mr-1" />Source</label><input className="il-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="lead-source" /></div>
        <div><label className="il-label"><DollarSign className="w-3 h-3 inline mr-1" />Deal Value (AUD)</label><input className="il-input font-mono" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="lead-value" /></div>
        <div className="sm:col-span-2"><label className="il-label">Notes</label><textarea className="il-input py-3" style={{ minHeight: "5rem" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-notes" /></div>
        <div className="sm:col-span-2"><button onClick={save} className="btn-signal h-11 px-5 font-head flex items-center gap-2" data-testid="lead-save"><Save className="w-4 h-4" /> Save Changes</button></div>
      </div>

      {/* Email follow-up */}
      <div className="card p-5 mb-4 border-l-4 border-[#FFB800]/50" data-testid="lead-email-panel">
        <p className="font-head font-700 uppercase text-sm flex items-center gap-2"><Send className="w-4 h-4 text-[#FFB800]" /> Send follow-up</p>
        {form.email ? (
          <>
            <p className="font-body text-sm text-[#94A3B8] mt-1">Sends a branded IronLog email to <span className="text-white">{form.email}</span>, logged in the timeline below. Pick a template and add an optional personal line.</p>
            <select className="il-input mt-3" value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="lead-email-template">
              {EMAIL_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <textarea className="il-input py-3 mt-2" style={{ minHeight: "5rem" }} placeholder="Optional personal note to add to the email…" value={emailNote} onChange={(e) => setEmailNote(e.target.value)} data-testid="lead-email-note-input" />
            <button onClick={sendEmail} disabled={sending} className="btn-signal h-11 px-5 font-head flex items-center gap-2 mt-3 disabled:opacity-60" data-testid="lead-email-send"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Email"}</button>
            <p className="font-mono text-[11px] text-[#94A3B8] mt-3">Emails use fixed IronLog templates (transactional). Open &amp; click tracking needs a dedicated email service (Resend / Brevo) with a verified domain — share those and I&apos;ll switch on tracking.</p>
          </>
        ) : (
          <p className="font-body text-sm text-[#94A3B8] mt-1">Add an email address above to send this lead a follow-up.</p>
        )}
      </div>

      {/* Activity timeline */}
      <div className="card" data-testid="lead-activity">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2"><Clock className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Activity</h2></div>
        <div className="p-4 flex gap-2 border-b border-white/10">
          <select className="il-input h-11 w-32 shrink-0" value={actType} onChange={(e) => setActType(e.target.value)} data-testid="lead-activity-type">
            <option value="note">Note</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option>
          </select>
          <input className="il-input" placeholder="Log a call, note or meeting…" value={activity} onChange={(e) => setActivity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addActivity()} data-testid="lead-activity-input" />
          <button onClick={addActivity} className="btn-signal h-11 px-4 font-head shrink-0" data-testid="lead-activity-add">Log</button>
        </div>
        <div className="divide-y divide-white/10">
          {[...(lead.activities || [])].reverse().map((a) => {
            const Icon = ACT_ICON[a.type] || StickyNote;
            return (
              <div key={a.id} className="px-5 py-3 flex items-start gap-3" data-testid={`lead-activity-${a.id}`}>
                <Icon className="w-4 h-4 text-[#FFB800] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm">{a.text}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">{a.actor} · {fmtDateTime(a.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
